import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OneWebComponentsAngularModule } from '@one/angular';
import { Validators, Editor, Toolbar, schema, toDoc, toHTML, NgxEditorModule } from 'ngx-editor';

@Component({
  selector: 'dl-rich-text-document',
   standalone: true,
    imports:[OneWebComponentsAngularModule, NgxEditorModule,FormsModule,ReactiveFormsModule],
  templateUrl: './rich-text-document.component.html',
  styleUrl: './rich-text-document.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class RichTextDocumentComponent implements AfterViewInit {

  public editor: Editor;
  public toolbar: Toolbar = [['bold', 'italic', 'underline'], ['ordered_list', 'bullet_list'], ['link']];
  public jsonDoc;
  public keyupFlagstatus = false;
  private _editorReady = false;
  private _lastEmittedHTML: string = ''; // Track last emitted HTML to detect circular updates

  private _jsonTextItem: any = '';
  public _showInEditorItem = '';

  @ViewChild('editorRef', { read: ElementRef }) editorElRef: ElementRef;

  constructor(private cdr: ChangeDetectorRef) {}

  @Input()
  public get jsonTextItem(): any {
    return this._jsonTextItem;
  }
  public set jsonTextItem(jsonTextItemP: any) {
    const newValue = jsonTextItemP ?? '';
    
    // Skip re-rendering if the incoming value matches what the editor just produced
    // This breaks the circular loop: editor change → parent setRTEText → setter → setValue → focus lost
    if (this._editorReady && newValue === this._lastEmittedHTML) {
      this._jsonTextItem = newValue;
      return;
    }
    
    this._jsonTextItem = newValue;
    if (this._editorReady) {
      this.getHTMLTextFromJSON();
    }
  }

  @Input()
  public get showInEditor(): string {
    return this._showInEditorItem;
  }
  public set showInEditor(edP: string) {
    this._showInEditorItem = edP;
    if (this._editorReady) {
      this.getHTMLTextFromJSON();
    }
  }

  @Output() public jsonTextReturn = new EventEmitter<any>();

  public edform = new FormGroup({
    editorContent: new FormControl({ value: '', disabled: false }, Validators.required())
  });

  public get doc(): AbstractControl {
    return this.edform.get('editorContent');
  }

  public ngOnInit(): void {
    this.editor = new Editor();
    this.edform.get('editorContent').valueChanges.subscribe(chValue => {
      if (typeof chValue === 'string') {
        this.jsonDoc = toDoc(chValue, schema);
      } else {
        this.jsonDoc = chValue;
      }
      // Track the HTML we're about to emit so we can detect circular updates
      try {
        this._lastEmittedHTML = typeof chValue === 'string' ? chValue : toHTML(chValue, schema);
      } catch {
        this._lastEmittedHTML = '';
      }
      this.getUpdatedText();
    });

    this._editorReady = true;
    if (this._jsonTextItem) {
      this.getHTMLTextFromJSON();
    }
  }

  public ngAfterViewInit(): void {
    // Ensure the editor is focusable after view init
    this.cdr.detectChanges();
  }

  public getUpdatedText(): void {
    this.jsonTextReturn.emit(this.jsonDoc);
  }

  public getHTMLTextFromJSON(): void {
    const value = this._jsonTextItem ?? '';
    this.edform.get('editorContent').setValue(value, { emitEvent: false });

    if (this.showInEditor === 'template') {
      this.edform.get('editorContent').disable();
    } else {
      this.edform.get('editorContent').enable();
    }
  }

  public editorkeydownEvent(evt) {
    this.keyupFlagstatus = true;
    this.getUpdatedText();
  }

  /**
   * Programmatically focus the ProseMirror editor when the editor container is clicked.
   * This is needed because owc-modal-dialog can intercept focus/click events,
   * preventing the contenteditable ProseMirror div from receiving focus naturally.
   */
  public focusEditor(event: MouseEvent): void {
    // Don't interfere with toolbar button clicks
    const target = event.target as HTMLElement;
    if (target.closest('.NgxEditor__MenuBar')) {
      return;
    }
    
    // Focus the ProseMirror contenteditable element
    if (this.editor) {
      this.editor.view.focus();
    }
  }

  public ngOnDestroy(): void {
    this.editor.destroy();
  }
}

